<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-[#000000A6] px-4"
    @click.self="emit('close')"
  >
    <section
      ref="dialogRef"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="comparisonModalHeadingId"
      :aria-describedby="!isLoading && comparisonDescription ? comparisonModalDescriptionId : undefined"
      class="flex max-h-[90vh] w-full max-w-[998px] flex-col overflow-hidden rounded-[8px] bg-white text-[#191919] shadow-lg md:h-[612px]"
    >
      <header class="mb-5 flex shrink-0 items-start justify-between gap-[10px] bg-white px-4 pb-4 pt-4">
        <div class="flex min-w-0 max-w-[908px] flex-1 flex-col gap-2">
          <h3
            :id="comparisonModalHeadingId"
            class="font-['Visuelt_Pro'] text-[16px] font-[500] leading-[130%] text-[#191919]"
          >
            {{ comparisonTitle }}
          </h3>

          <div v-if="isLoading" class="flex flex-col gap-1" aria-hidden="true">
            <div class="h-2 w-full animate-pulse rounded-sm bg-[#EAEAEA80]"></div>
            <div class="h-2 w-1/6 animate-pulse rounded-sm bg-[#EAEAEA80]"></div>
          </div>
          <p
            v-else-if="comparisonDescription"
            :id="comparisonModalDescriptionId"
            class="font-['Visuelt_Pro'] text-[16px] font-[400] leading-[140%] tracking-[-0.08px] text-[#555]"
          >
            {{ comparisonDescription }}
          </p>
        </div>

        <button
          ref="closeButton"
          type="button"
          class="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-[#29221D]"
          aria-label="Close comparison modal"
          @click="emit('close')"
        >
          <Icon
            icon="times"
            class="h-4 w-4"
          />
        </button>
      </header>

      <div
        v-if="isLoading"
        class="grid min-h-0 flex-1 grid-cols-3 gap-1 overflow-y-auto px-4 pb-4 pt-9"
        aria-busy="true"
        aria-live="polite"
      >
        <span class="sr-only">Comparison products are loading</span>
        <div v-for="column in 3" :key="column" class="min-w-0">
          <div class="h-[194px] animate-pulse rounded-[4px] bg-[#EAEAEA80]"></div>
          <div class="mt-1 h-5 animate-pulse rounded-sm bg-[#EAEAEA80]"></div>
          <div class="mt-1 h-2 animate-pulse rounded-sm bg-[#EAEAEA80]"></div>
          <div class="mt-1 h-2 w-1/2 animate-pulse rounded-sm bg-[#EAEAEA80]"></div>
          <div class="mt-6 h-2 animate-pulse rounded-sm bg-[#EAEAEA80]"></div>
          <div class="mt-1 h-2 w-1/2 animate-pulse rounded-sm bg-[#EAEAEA80]"></div>
        </div>
      </div>

      <template v-else-if="showComparisonSection">
        <div class="comparison-scrollbar-hidden relative min-h-0 flex-1 overflow-y-auto">
          <div
            ref="comparisonScrollContainer"
            data-test="comparison-scroll-container"
            class="comparison-scrollbar-hidden flex overflow-x-auto overflow-y-hidden px-6 pb-4"
            @scroll="updateActiveComparisonPage"
          >
            <ul
              class="grid w-max grid-flow-col auto-cols-[300.66px] gap-0"
              :class="comparisonProducts.length <= 2 ? 'mx-auto' : ''"
              :style="{ gridTemplateRows: comparisonGridRows }"
            >
              <li
                v-for="product in comparisonProducts"
                :key="product.id ?? product.sku"
                class="row-span-full grid grid-rows-subgrid overflow-hidden border-[#EAEAEA] bg-white first:rounded-l-[8px] last:rounded-r-[8px]"
                :class="{
                  'rounded-[8px] border border-[#F7A14C] bg-[#FBFBFB]':
                    isRecommendedProduct(product)
                }"
                :data-recommended="isRecommendedProduct(product) || undefined"
              >
                <div class="p-4">
                  <div
                    class="flex min-h-[282px] flex-col justify-between rounded-[8px] bg-[#F6F8FA] p-4"
                  >
                    <span
                      v-if="getBadgeText(product)"
                      class="flex items-center gap-1 font-['Visuelt_Pro'] text-[12px] font-[400] leading-[130%] text-[#191919]"
                    >
                      <Icon
                        v-if="isRecommendedProduct(product)"
                        icon="star"
                        class="h-4 w-4 text-[#F7A14C]"
                        aria-hidden="true"
                      />
                      {{ getBadgeText(product) }}
                    </span>

                    <NuxtLink
                      v-if="getProductImageUrl(product) && getProductUrl(product)"
                      :to="getProductUrl(product)"
                      class="mx-auto cursor-pointer"
                      :aria-label="`View ${getProductName(product)}`"
                    >
                      <img
                        :src="getProductImageUrl(product)"
                        :alt="getProductName(product)"
                        class="h-[190px] max-w-full object-contain"
                      >
                    </NuxtLink>

                    <img
                      v-else-if="getProductImageUrl(product)"
                      :src="getProductImageUrl(product)"
                      :alt="getProductName(product)"
                      class="mx-auto h-[190px] max-w-full object-contain"
                    >

                    <div
                      v-else
                      class="flex h-[190px] items-center justify-center text-center text-[14px] text-[#555]"
                    >
                      {{ getProductName(product) }}
                    </div>

                    <div class="flex items-center justify-between gap-3">
                      <span
                        class="font-['Visuelt_Pro'] text-[12px] font-[400] leading-[130%] text-[#555]"
                      >
                        {{ getProductPrice(product) }}
                      </span>

                      <button
                        type="button"
                        class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-[4px] bg-[#29221D] p-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
                        :aria-label="`Add ${getProductName(product)} to cart`"
                        :disabled="isAddingProduct(product)"
                        :aria-busy="isAddingProduct(product)"
                        @click.prevent="emit('add-to-cart', product)"
                      >
                        <Icon
                          icon="cart-plus"
                          class="h-4 w-4"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>

                  <div class="mt-2 flex flex-col gap-1">
                    <NuxtLink
                      v-if="getProductUrl(product)"
                      :to="getProductUrl(product)"
                      class="w-fit cursor-pointer font-['Visuelt_Pro'] text-[20px] font-[400] leading-[120%] tracking-[-0.1px] text-[#191919] hover:text-inherit"
                    >
                      {{ getProductName(product) }}
                    </NuxtLink>

                    <h4
                      v-else
                      class="font-['Visuelt_Pro'] text-[20px] font-[400] leading-[120%] tracking-[-0.1px] text-[#191919]"
                    >
                      {{ getProductName(product) }}
                    </h4>

                    <p
                      class="font-['Visuelt_Pro'] text-[12px] font-[400] leading-[130%] text-[#555]"
                    >
                      {{ formatBenefits(getProductBenefits(product)) }}
                    </p>

                    <StarRating
                      v-if="hasProductRating(product)"
                      class="w-fit"
                      :rating="product.averageRating!"
                    />
                  </div>
                </div>

                <dl v-if="props.comparisonData?.comparison.length" class="row-[2/-1] grid grid-rows-subgrid">
                  <div
                    v-for="row in getComparisonRows(product)"
                    :key="row.label"
                    class="min-h-[84px] border-t border-[#EAEAEA] px-4 py-4"
                  >
                    <dt
                      class="font-mono text-[10px] font-[400] uppercase leading-none text-[#999999]"
                    >
                      {{ row.label }}
                    </dt>

                    <dd
                      class="mt-1 font-['Visuelt_Pro'] text-[16px] font-[400] leading-[140%] tracking-[-0.08px] text-[#555]"
                    >
                      {{ row.value }}
                    </dd>
                  </div>
                </dl>
              </li>
            </ul>
          </div>

          <button
            v-if="comparisonProducts.length > visibleCards"
            type="button"
            class="absolute left-2 top-[157px] z-10 hidden h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black drop-shadow-md transition disabled:cursor-not-allowed disabled:opacity-40 md:flex"
            aria-label="Previous comparison page"
            :disabled="isFirstComparisonPage"
            @click="showPreviousComparisonPage"
          >
            <Icon
              class="mt-1.5 text-xl"
              icon="arrow-left"
            />
          </button>

          <button
            v-if="comparisonProducts.length > visibleCards"
            type="button"
            class="absolute right-2 top-[157px] z-10 hidden h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black drop-shadow-md transition disabled:cursor-not-allowed disabled:opacity-40 md:flex"
            aria-label="Next comparison page"
            :disabled="isLastComparisonPage"
            @click="showNextComparisonPage"
          >
            <Icon
              class="mt-1.5 text-xl"
              icon="arrow-right"
            />
          </button>
        </div>

        <div
          v-if="comparisonPageCount > 1"
          class="flex items-center justify-center px-6 pb-6"
        >
          <div
            class="flex justify-center gap-1"
            aria-label="Comparison pages"
          >
            <button
              v-for="pageIndex in comparisonPageIndexes"
              :key="pageIndex"
              type="button"
              class="h-[6px] cursor-pointer rounded-[4px] border-0 p-0"
              :class="
                pageIndex === activeComparisonPage
                  ? 'w-6 bg-[#123C24]'
                  : 'w-2 bg-[#E0E0E0]'
              "
              :aria-label="`Go to comparison page ${pageIndex + 1}`"
              :aria-current="
                pageIndex === activeComparisonPage
                  ? 'true'
                  : undefined
              "
              @click="scrollToComparisonPage(pageIndex)"
            />
          </div>
        </div>
      </template>

      <div
        v-else
        class="px-6 pb-8"
      >
        <p
          class="font-['Visuelt_Pro'] text-[16px] font-[400] leading-[140%] tracking-[-0.08px] text-[#555]"
        >
          Product comparison is not available right now. Please try again.
        </p>
      </div>
    </section>
  </div>
</template>
<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from 'vue'

import Icon from '~/components/ui/atom/icon.vue'
import StarRating from '~/components/ui/atom/starRating.vue'

import type {
  AgenticProduct,
  FilterCompareResponse
} from '~~/shared/types/common'

const props = defineProps<{
  isOpen: boolean
  isLoading: boolean
  comparisonData: FilterCompareResponse | null
  addingProductSku?: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'add-to-cart', product: AgenticProduct): void
}>()

const comparisonModalHeadingId =
  'agentic-comparison-modal-heading'

const comparisonModalDescriptionId =
  'agentic-comparison-modal-description'

const comparisonScrollContainer =
  ref<HTMLElement | null>(null)

const dialogRef = ref<HTMLElement | null>(null)

const closeButton =
  ref<HTMLButtonElement | null>(null)

const resizeObserver =
  ref<ResizeObserver | null>(null)

const activeComparisonPage = ref(0)

const visibleCards = ref(1)

const lastFocusedElement =
  ref<HTMLElement | null>(null)

let previousBodyOverflow: string | null = null

const lockBackgroundScroll = () => {
  if (!import.meta.client || previousBodyOverflow !== null) return
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
}

const restoreBackgroundScroll = () => {
  if (!import.meta.client || previousBodyOverflow === null) return
  document.body.style.overflow = previousBodyOverflow
  previousBodyOverflow = null
}

const comparisonTitle = computed(
  () =>
    props.comparisonData?.title ||
    'Compare products'
)

const comparisonDescription = computed(
  () =>
    props.comparisonData?.short_description ?? ''
)

const comparisonProducts = computed(
  () => props.comparisonData?.products || []
)

const showComparisonSection = computed(
  () => comparisonProducts.value.length > 0
)

const comparisonGridRows = computed(() => [
  'auto',
  ...(props.comparisonData?.comparison || []).map(() => 'minmax(84px, auto)')
].join(' '))

const comparisonPageCount = computed(() => {
  const totalCards =
    comparisonProducts.value.length

  return Math.max(
    1,
    totalCards - visibleCards.value + 1
  )
})

const comparisonPageIndexes = computed(() =>
  Array.from(
    { length: comparisonPageCount.value },
    (_, index) => index
  )
)

const isFirstComparisonPage = computed(
  () => activeComparisonPage.value === 0
)

const isLastComparisonPage = computed(
  () =>
    activeComparisonPage.value ===
    comparisonPageCount.value - 1
)

const getProductName = (
  product: AgenticProduct
) =>
  product.name ||
  product.productName ||
  'Product'

const getProductImageUrl = (
  product: AgenticProduct
) =>
  product.primaryImageUrl ||
  product.imageUrl ||
  product.imageUrls?.[0] ||
  ''

const getProductUrl = (
  product: AgenticProduct
) => product.url || ''

const getBadgeText = (
  product: AgenticProduct
) => {
  if (
    props.comparisonData?.recommended_sku ===
    product.sku
  ) {
    return 'Recommended'
  }

  return (
    product.customBadges?.find(Boolean) ??
    product.badges?.find(Boolean)
  )
}

const isRecommendedProduct = (
  product: AgenticProduct
) =>
  !!props.comparisonData?.recommended_sku &&
  product.sku ===
    props.comparisonData.recommended_sku

const hasProductRating = (
  product: AgenticProduct
) =>
  typeof product.averageRating ===
    'number' &&
  Number.isFinite(product.averageRating)

const isAddingProduct = (
  product: AgenticProduct
) =>
  !!props.addingProductSku &&
  product.sku === props.addingProductSku

const getProductBenefits = (
  product: AgenticProduct
) => {
  const fallbackBenefits = (
    product as AgenticProduct & {
      benifits?: string[] | string
    }
  ).benifits

  return (
    product.benefits ??
    fallbackBenefits
  )
}

const getProductPrice = (
  product: AgenticProduct
) => {
  const numericPrice =
    typeof product.salePrice === 'number'
      ? product.salePrice
      : typeof product.retailPrice === 'number'
        ? product.retailPrice
        : typeof product.wholesalePrice ===
            'number'
          ? product.wholesalePrice
          : typeof product.price === 'string'
            ? Number.parseFloat(product.price)
            : Number.NaN

  if (Number.isFinite(numericPrice)) {
    return `$${numericPrice.toFixed(2)}`
  }

  return (
    product.price ||
    'Price unavailable'
  )
}

const formatBenefits = (
  benefits:
    | string[]
    | string
    | undefined
) => {
  if (Array.isArray(benefits)) {
    return benefits
      .filter(Boolean)
      .join(', ')
  }

  if (typeof benefits === 'string') {
    return benefits
  }

  return 'Benefits unavailable'
}

const formatComparisonValue = (
  value:
    | string
    | number
    | boolean
    | string[]
    | null
    | undefined
) => {
  if (Array.isArray(value)) {
    return value.join(', ')
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '--'
  }

  return String(value)
}

const getComparisonRows = (
  product: AgenticProduct
) =>
  (
    props.comparisonData?.comparison || []
  ).map(row => ({
    label: row.label,
    value: formatComparisonValue(
      row.values?.[product.sku]
    )
  }))

const updateVisibleCards = () => {
  const container =
    comparisonScrollContainer.value

  if (!container) return

  const card =
    container.querySelector(
      'li'
    ) as HTMLElement | null

  if (!card) return

  visibleCards.value = Math.max(
    1,
    Math.floor(
      container.clientWidth /
        card.offsetWidth
    )
  )
}

const updateActiveComparisonPage =
  () => {
    const container =
      comparisonScrollContainer.value

    if (!container) return

    const maxScroll =
      container.scrollWidth -
      container.clientWidth

    if (maxScroll <= 0) {
      activeComparisonPage.value = 0
      return
    }

    activeComparisonPage.value =
      Math.round(
        (container.scrollLeft /
          maxScroll) *
          (comparisonPageCount.value - 1)
      )
  }

const scrollToComparisonPage = (
  pageIndex: number
) => {
  const container =
    comparisonScrollContainer.value

  if (!container) return

  const targetPage = Math.min(
    Math.max(pageIndex, 0),
    comparisonPageCount.value - 1
  )

  activeComparisonPage.value =
    targetPage

  const maxScroll =
    container.scrollWidth -
    container.clientWidth

  container.scrollTo({
    left:
      comparisonPageCount.value > 1
        ? (maxScroll * targetPage) /
          (comparisonPageCount.value - 1)
        : 0,
    behavior: 'smooth'
  })
}

const showPreviousComparisonPage =
  () => {
    scrollToComparisonPage(
      activeComparisonPage.value - 1
    )
  }

const showNextComparisonPage = () => {
  scrollToComparisonPage(
    activeComparisonPage.value + 1
  )
}

const trapFocus = (
  event: KeyboardEvent
) => {
  if (
    !props.isOpen ||
    event.key !== 'Tab'
  ) {
    return
  }

  const dialog = dialogRef.value

  if (!dialog) return

  const focusableElements =
    dialog.querySelectorAll<HTMLElement>(
      `
      button:not([disabled]),
      a[href],
      input:not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      not([tabindex="-1"])
    `
    )

  const elements =
    Array.from(focusableElements)

  if (!elements.length) return

  const first = elements[0]
  const last =
    elements[elements.length - 1]

  if (!first || !last) return

  if (
    event.shiftKey &&
    document.activeElement === first
  ) {
    event.preventDefault()
    last.focus()
    return
  }

  if (
    !event.shiftKey &&
    document.activeElement === last
  ) {
    event.preventDefault()
    first.focus()
  }
}

watch(
  () => props.isOpen,
  async isOpen => {
    if (!import.meta.client) return

    if (!isOpen) {
      activeComparisonPage.value = 0
      restoreBackgroundScroll()

      lastFocusedElement.value?.focus()

      return
    }

    lastFocusedElement.value =
      document.activeElement as HTMLElement

    lockBackgroundScroll()
    await nextTick()
    if (!props.isOpen) return

    updateVisibleCards()
    updateActiveComparisonPage()

    if (
      comparisonScrollContainer.value &&
      resizeObserver.value
    ) {
      resizeObserver.value.disconnect()

      resizeObserver.value.observe(
        comparisonScrollContainer.value
      )
    }

    closeButton.value?.focus()
  },
  { immediate: true }
)

watch(
  comparisonProducts,
  async () => {
    await nextTick()

    updateVisibleCards()
    updateActiveComparisonPage()
  }
)

watch(comparisonScrollContainer, (container) => {
  resizeObserver.value?.disconnect()
  if (!container) return

  updateVisibleCards()
  updateActiveComparisonPage()
  resizeObserver.value?.observe(container)
}, { flush: 'post' })

onMounted(() => {
  document.addEventListener(
    'keydown',
    trapFocus
  )

  resizeObserver.value =
    new ResizeObserver(() => {
      updateVisibleCards()
      updateActiveComparisonPage()
    })

  if (
    comparisonScrollContainer.value
  ) {
    resizeObserver.value.observe(
      comparisonScrollContainer.value
    )
  }

  updateVisibleCards()
})

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect()
  restoreBackgroundScroll()

  document.removeEventListener(
    'keydown',
    trapFocus
  )
})
</script>
<style scoped>
.comparison-scrollbar-hidden {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.comparison-scrollbar-hidden::-webkit-scrollbar {
  display: none;
}
</style>